import {DefaultThemeColors, type ThemeColorsInterface} from "~~/server/types/settings";

type RGBA = { r: number; g: number; b: number; a: number };

export function useDesign() {
  const clamp255 = (n: number) => Math.min(255, Math.max(0, Math.round(n)));
  const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

  const parseHex = (raw: string): RGBA | null => {
    let hex = raw.trim().replace(/^#/, "");
    if (!/^[0-9a-fA-F]+$/.test(hex)) return null;

    // #RGB
    if (hex.length === 3) {
      hex = hex.split("").map((c) => c + c).join("");
    }

    // #RRGGBB
    if (hex.length === 6) {
      const num = parseInt(hex, 16);
      return {
        r: (num >> 16) & 0xff,
        g: (num >> 8) & 0xff,
        b: num & 0xff,
        a: 1,
      };
    }

    return null;
  };

  const parseRgbFunc = (raw: string): RGBA | null => {
    const s = raw.trim();
    // supports: rgb(1,2,3) / rgba(1,2,3,0.5) (with spaces)
    const m = s.match(/^rgba?\(\s*([^)]+)\s*\)$/i);
    if (!m) return null;

    const parts = m[1]!.split(",").map((p) => p.trim());
    if (parts.length !== 3 && parts.length !== 4) return null;

    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    if (![r, g, b].every((v) => Number.isFinite(v))) return null;

    const a = parts.length === 4 ? Number(parts[3]) : 1;
    if (!Number.isFinite(a)) return null;

    return { r: clamp255(r), g: clamp255(g), b: clamp255(b), a: clamp01(a) };
  };

  const toRgba = (color: string): RGBA => {
    const c = color?.trim?.() ?? "";
    return (
      parseHex(c) ??
      parseRgbFunc(c) ??
      { r: 0, g: 0, b: 0, a: 1 }
    );
  };

  const rgbToHex = (r: number, g: number, b: number) =>
    "#" +
    [r, g, b]
      .map((v) => clamp255(v).toString(16).padStart(2, "0"))
      .join("");


  const mix = (color1: string, color2: string, weight: number) => {
    const w = clamp01(weight);
    const c1 = toRgba(color1);
    const c2 = toRgba(color2);

    const r = c1.r * w + c2.r * (1 - w);
    const g = c1.g * w + c2.g * (1 - w);
    const b = c1.b * w + c2.b * (1 - w);

    return rgbToHex(r, g, b);

  };

  const createVariants = (primary: string, slug: string): Record<string, string> => {
    const white = "#FFFFFF";
    const black = "#000000";

    const vars: Record<string, string> = {};

    // converts to hex safely
    vars[`--el-color-${slug}`] = mix(primary, primary, 1);

    for (let i = 1; i <= 9; i++) {
      const weight = i / 10;
      vars[`--el-color-${slug}-light-${i}`] = mix(white, primary, weight);
      vars[`--el-color-${slug}-dark-${i}`] = mix(black, primary, weight);
    }

    return vars;
  };

  const createThemeColors = (settings_colors: ThemeColorsInterface = DefaultThemeColors) => {
    const colors = settings_colors || DefaultThemeColors;

    return {
      "--nav-bg": colors?.menu?.bg || DefaultThemeColors.menu.bg,
      "--nav-text": colors?.menu?.text || DefaultThemeColors.menu.text,
      "--nav-text-hover":
        colors?.menu?.text_hover || DefaultThemeColors.menu.text_hover,
      "--nav-panel-bg":
        colors?.menu?.mega?.bg || DefaultThemeColors.menu.mega.bg,
      "--nav-panel-text":
        colors?.menu?.mega?.text || DefaultThemeColors.menu.mega.text,
      "--nav-panel-text-hover":
        colors?.menu?.mega?.text_hover ||
        DefaultThemeColors.menu.mega.text_hover,
      "--nav-border": colors?.menu?.border || DefaultThemeColors.menu.border,
      "--nav-mobile-bg":
        colors?.menu?.mobile?.background ||
        DefaultThemeColors.menu.mobile.background,
      "--nav-mobile-color":
        colors?.menu?.mobile?.color || DefaultThemeColors.menu.mobile.color,

      "--link-color": colors?.link || DefaultThemeColors.link,
      "--footer-bg": colors?.footer?.bg || DefaultThemeColors.footer.bg,
      "--footer-color":
        colors?.footer?.color || DefaultThemeColors.footer.color,
      "--body-bg": colors?.body?.bg || DefaultThemeColors.body.bg,
      "--body-color": colors?.body?.color || DefaultThemeColors.body.color,
      ...(colors?.primary_button_text?.trim()
        ? { "--theme-primary-button-text": colors.primary_button_text }
        : {}),
      ...createVariants(
        colors?.primary || DefaultThemeColors.primary,
        "primary",
      ),
      ...createVariants(colors?.info || DefaultThemeColors.info, "info"),
    } as Record<string, string>;
  };

  return { createThemeColors };
}
